import type { SourcePos } from '../../ast.ts';
import { ShapeError } from '../../errors.ts';
import { astShape } from '../../normalize.ts';
import { parseSortEntry, type SortEntry } from '../sort-entry.ts';

/**
 * One parsed `:sort` key, consumed by the host's CDF fetcher
 * (`translateSortToCdf`) to build the instance-query sort spec. Aliased
 * to the engine-wide `SortEntry` so the `{ column, descending }` shape
 * has a single source of truth across `sort` and `:sort`.
 */
export type QkgSortKey = SortEntry;

/**
 * Single source of truth for the `query-knowledge-graph` `:sort` shape
 * *container*: a vector whose entries are each classified by the shared
 * `parseSortEntry` kernel (bare column ref → ascending; `(desc col)` →
 * descending). Anything else throws a `ShapeError` tagged with
 * `argRef: ':sort'` so the message renders `argument :sort: …`, the
 * suggestion points the agent at the keyword, and the tool layer
 * classifies it as a retryable `InvalidExpressionError` (not an
 * `UnexpectedError`).
 *
 * Invoked twice on purpose: eagerly in `qkg`'s `apply` (fail-fast at
 * op-eval, with a real `SourcePos`) and again in the host fetcher
 * (`NO_POS`, defence-in-depth for direct JS-object `AstInput` that
 * bypasses the op). The per-entry shape rule lives in
 * `parseSortEntry`; this function owns only the vector container +
 * `:sort` error policy.
 */
export const parseQkgSort = (sort: unknown, pos: SourcePos): readonly QkgSortKey[] => {
  const top = astShape(sort);
  if (top.kind !== 'vector') {
    throw new ShapeError(`expected a vector of column refs / \`(desc col)\` wrappers, got ${top.kind}`, pos, ':sort');
  }
  return top.elements.map((entry, i): QkgSortKey => {
    const r = parseSortEntry(entry);
    if (r.ok) {
      return { column: r.column, descending: r.descending };
    }
    if (r.reason === 'bad-desc-inner') {
      throw new ShapeError(`sort entry ${i}: \`(desc …)\` takes a single column ref, got ${r.got}`, pos, ':sort');
    }
    throw new ShapeError(`sort entry ${i}: expected a column ref or \`(desc col)\`, got ${r.got}`, pos, ':sort');
  });
};
